/**
 * 이상한 방 - 매치 세션의 파티 화면 묶음 (여럿이에만 있는 레이어의 조립 · 프레임 갱신 · 관전 카메라 · 문 소리 · 리빌 이름표)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제 - 관전) · 4.4절(원래 방 정원 1) · 5절(웃김 설계 포인트 - 관전 · 문 앞 줄 · 리빌)
 *            `이상한방-상태모델-v0.1.md` 9.3절(봇은 새 코드 경로를 내지 않는다) · 11.3절(카메라 추종 · 연출은 클라이언트의 것)
 *            로드맵 M5(파티 감 - 관전 · 문 앞 줄 · 리빌, 봇만으로 완성)
 *
 * `matchSession.ts`에서 갈라져 나왔다(400줄). 여럿이에만 있는 화면 여섯(참가자 줄 · 구획 진행판 · 걸기 배너 · 리빌 이름표 · 시점 줄 ·
 * 문 앞 줄)을 **한 곳에서** 만들고 같은 프레임에 같은 뷰로 갱신한다. 세션에는 `layers`(켜고 끄기)와 세 개의 문(`render` · `onFoundChange` ·
 * `onRevealStep`)만 남는다. 원칙은 세션과 같다 - **규칙이 하나도 없다.** 누구를 관전할 수 있는지(`spectatable`) · 문 앞의 남은 시간
 * (`door`) · 누가 찾았는지(`found[].by`)는 전부 호스트가 뷰에 내린 값이고, 여기는 그것을 화면 모듈의 모양으로 옮기는 순수 함수
 * (`matchSessionView` · `matchSessionReveal`)를 부른 뒤 그리기만 한다.
 *
 * ## 관전 카메라는 발견 위치를 따라간다
 *
 * 관전 대상이 무언가 찾으면(`foundChange.by === spectateTarget`) 카메라를 **그 물건의 자리**로 보낸다(`iso.focusOn`). 봇에게는 조준
 * 신호(presence)가 없으므로 따라갈 수 있는 것이 발견 위치뿐이고, 사람 술래도 같은 신호를 탄다 - 봇과 사람이 다른 경로를 타면 봇 판에서
 * 검증한 화면이 서버 판에서 다르다(9.3절). 그 사람의 발견은 시점 줄의 티커에도 한 줄 쌓인다. 물건이 두 방 어디에도 없으면(스냅샷 전)
 * 카메라는 움직이지 않는다 - 엉뚱한 곳을 비추는 것보다 안 움직이는 쪽이 낫다.
 *
 * ## 문이 열리는 소리는 뷰의 전이에서 난다
 *
 * `doorOpen`은 문 뷰의 보유자가 **있음 → 없음**으로 바뀌는 순간, 그때 내가 줄에 서 있었을 때만 난다. 효과가 아니라 뷰 두 장의 비교로
 * 정하는 이유는 리듀서가 이 소리를 내지 않기 때문이다(`doorChanged`는 전원에게 가는 상태 알림이고, 그중 "나에게 사건인 것"을 고르는
 * 것은 연출이다). 수색 밖의 전이(라운드가 접혀 문이 비는 것)는 사건이 아니라 건너뛴다. 규칙은 없다 - 누가 나갔는지도 내가 줄에 있는지도
 * 호스트의 값이다.
 *
 * ## 리빌 마지막의 소리
 *
 * "아무도 못 봤다" 대형 스탬프(`juice.nobodySaw`, 마지막 미발견에만)에 `roundFail`을 얹는다 - 하강 3음은 "전원이 놓쳤다"의 소리이고, 관전
 * 진입(`spectate-enter`)과 같은 사전 항목이라 "탄환이 바닥났다 · 다 놓쳤다"가 한 가족으로 들린다. 전용 소리를 만들지 않은 결정은
 * 앞 몫의 기록에 있다(문서는 `doorOpen` 1종만 요구했다). 리빌 도장 소리(`reveal`)는 스텝마다 그대로 나므로 이 소리는 그 위에 얹힌다.
 *
 * 접착층의 한 조각이라 `ui/`·`input/`을 부른다(CLAUDE.md의 의도된 예외 - `matchSession*`).
 */

import { Vector3 } from 'three'

import type { Catalog } from '../core/room'
import { t } from '../i18n'
import type { IsoCamera } from '../input/isoCamera'
import { computeObjectCenterWorld } from '../render/objectView'
import { createZoneProgress } from '../ui/zoneProgress'
import { createDoorQueue } from '../ui/doorQueue'
import type { JuiceHandle } from '../ui/juice'
import { createPlayerStrip } from '../ui/playerStrip'
import { createRevealParty, type RevealPartyHandle } from '../ui/revealParty'
import { createSpectateBar } from '../ui/spectateBar'
import { createWagerBanner, type WagerBannerHandle } from '../ui/wagerBanner'
import type { MatchEffectBody, PlayerEvent } from './match/matchTypes'
import type { ClientView } from './match/matchView'
import { doorAfter } from './matchClock'
import { partyRevealFace } from './matchSessionReveal'
import type { MatchScene } from './matchSessionScene'
import { buildDoorQueueView, buildPlayerStripView, buildSpectateBarView, buildZoneProgressView, nameOf } from './matchSessionView'
import type { RevealStep } from './reveal'
import type { SessionAudio } from './sessionAudio'

/* --- 타입 ------------------------------------------------------------------- */

/** 파티 화면이 세션에서 빌려 쓰는 것 전부. 세션 클로저가 아니라 **이 인자만** 본다 - 옮긴 것이 배선임이 드러나게. */
export type MatchPartyParts = Readonly<{
  mount: HTMLElement
  iso: IsoCamera
  catalog: Catalog
  /** 대형 스탬프의 손(`nobodySaw`). 리빌 이름표가 빌려 찍는다(`ui/revealParty.ts`). */
  juice: Pick<JuiceHandle, 'nobodySaw'>
  audio: Pick<SessionAudio, 'play'>
  /** 물건의 자리 조회. 남의 발견 이름표(`matchSessionEffects`)와 같은 조회다. */
  scene: Pick<MatchScene, 'objectOf'>
  /** 이벤트 하나를 호스트로(세션의 `send`). 시점 버튼이 `spectate`를 보내는 문이다. */
  send: (event: PlayerEvent) => number
}>

/** 켰다 끄는 화면 레이어의 공통 모양. 세션이 잠들 때 한 번에 접고 깨어날 때 한 번에 편다. */
export type PartyLayer = Readonly<{ setVisible: (visible: boolean) => void; dispose: () => void }>

export type MatchParty = Readonly<{
  layers: readonly PartyLayer[]
  /** 걸기 배너. 효과(`raised{by}`)로 뜨므로 효과 번역기가 직접 부른다. */
  wagerBanner: WagerBannerHandle
  /** 리빌 이름표. 페이즈 처리가 리빌이 접힐 때 함께 걷는다(`matchSessionPhases`). */
  revealParty: RevealPartyHandle
  /** 같은 프레임에 같은 뷰로 전부 그린다. `sinceMs`는 마지막 `step` 이후 흐른 호스트 ms(`matchClock.sinceStepMs`). */
  render: (view: ClientView, sinceMs: number) => void
  /** `foundChange` 하나. 내가 따라가는 사람의 것이면 티커 한 줄 + 카메라 이동. */
  onFoundChange: (effect: Extract<MatchEffectBody, { type: 'foundChange' }>, view: ClientView) => void
  /** 리빌 스텝 진입(`sessionReveal.onStepEnter`). 얼굴을 찍고 마지막 미발견이면 소리를 얹는다. */
  onRevealStep: (step: RevealStep, index: number, steps: readonly RevealStep[], view: ClientView) => void
}>

/* --- 본체 ------------------------------------------------------------------- */

export function createMatchParty(parts: MatchPartyParts): MatchParty {
  const { mount, iso, catalog, audio } = parts
  /** 참가자 줄. HUD와 같은 프레임에 같은 뷰로 그린다 - 남의 발견·관전 전환은 효과가 아니라 뷰의 값이라 따로 배선하지 않는다. */
  const strip = createPlayerStrip({ mount })
  /** 구획 진행판(M11). 남들이 어디까지 왔는지는 효과가 아니라 뷰의 값(`PublicPlayer.changeUsed`)이다. */
  const zoneProgress = createZoneProgress({ mount })
  /** 걸기 배너(M7). 뷰가 아니라 효과(`raised{by}`)로 뜬다 - 누가 걸었다는 것은 상태가 아니라 순간이다. */
  const wagerBanner = createWagerBanner({ mount })
  /** 파티 리빌의 이름표(M5). 스텝마다 "OO이 찾았다" 또는 "아무도 못 봤다" - 대형 스탬프는 `juice.nobodySaw`를 빌려 찍는다. */
  const revealParty = createRevealParty({ mount, juice: parts.juice })
  /** 시점 줄(M5). 버튼은 `spectate`를 보낼 뿐이고 가부는 호스트다(`matchShoot.onSpectate`). */
  const spectateBar = createSpectateBar({ mount, onPick: (target) => parts.send({ type: 'spectate', target }) })
  /** 문 앞 줄(M5). 시간은 문 뷰 + 시계 보간에서만 온다(규칙 5). */
  const doorQueue = createDoorQueue({ mount })

  /** 문 소리의 재료 - 앞 프레임의 문. 보유자가 있었고 그때 내가 줄에 있었는가만 기억한다(머리말). */
  let lastDoor: Readonly<{ held: boolean; queued: boolean }> | null = null

  function maybeDoorOpened(view: ClientView): void {
    const held = view.door.holder !== null
    if (view.phase === 'hunt' && lastDoor !== null && lastDoor.held && !held && lastDoor.queued) audio.play('doorOpen')
    lastDoor = { held, queued: view.self.queuePosition !== null }
  }

  return {
    layers: [strip, zoneProgress, wagerBanner, revealParty, spectateBar, doorQueue],
    wagerBanner,
    revealParty,

    render(view: ClientView, sinceMs: number): void {
      strip.render(buildPlayerStripView(view))
      zoneProgress.render(buildZoneProgressView(view))
      spectateBar.render(buildSpectateBarView(view))
      doorQueue.render(buildDoorQueueView(view, doorAfter(view.door, sinceMs)))
      maybeDoorOpened(view)
    },

    onFoundChange(effect, view): void {
      // 내가 따라가는 사람의 것만. `spectateTarget`은 호스트가 굳힌 값이고 내 발견은 `spectateTarget`이 나일 수 없어 걸린다.
      if (!view.self.spectating || view.self.spectateTarget !== effect.by) return
      const obj = parts.scene.objectOf(effect.targetId)
      const entry = obj === undefined ? undefined : catalog.get(obj.catalogId)
      if (obj !== undefined && entry !== undefined) {
        const center = computeObjectCenterWorld(obj.cell, entry, obj.rot)
        iso.focusOn(new Vector3(center.x, center.y, center.z))
      }
      // 소품 이름은 카탈로그의 것이다(`reveal.ts`와 같은 결정). 못 찾은 것만 사전의 대체 이름으로 간다.
      spectateBar.pushFind({ name: nameOf(view, effect.by), item: entry?.name ?? t('reveal.unknownItem') })
    },

    onRevealStep(step, index, steps, view): void {
      // 얼굴은 순수 함수가 정하고(`matchSessionReveal.partyRevealFace`) 여기는 찍기만 한다.
      const face = partyRevealFace(step, index, steps, view)
      if (face === null) return
      revealParty.showStep(face)
      if (face.kind === 'nobody' && face.finale) audio.play('roundFail')
    },
  }
}
